import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlarmClock, CalendarCheck, ClipboardList, Hourglass, ListChecks, LogIn,
  LogOut, MapPin, ScanFace, Timer, TrendingUp, Users,
} from "lucide-react";
import {
  Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { AppShell } from "@/components/AppShell";
import { SystemHealthCard } from "@/components/SystemHealthCard";
import { FaceEngineLogPanel } from "@/components/FaceEngineLogPanel";
import { useLang } from "@/lib/i18n";
import { api } from "@/lib/api";

const StatTile = ({ icon: Icon, label, value, sub, tone = "default", testId }) => (
  <div data-testid={testId}
    className="flex items-start gap-3 rounded-xl border border-border bg-card p-4">
    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
      tone === "warn" ? "bg-amber-100 text-amber-700" : "bg-primary/10 text-primary"}`}>
      <Icon className="h-5 w-5" />
    </span>
    <div className="min-w-0">
      <p className="text-2xl font-semibold tabular-nums leading-tight">{value}</p>
      <p className="mt-0.5 text-xs font-medium text-muted-foreground">{label}</p>
      {sub && <p className="mt-0.5 truncate text-xs text-muted-foreground">{sub}</p>}
    </div>
  </div>
);

const pct = (v) => (v === null || v === undefined ? "–" : `${v}%`);

/* Dedicated System Health dashboard: infrastructure status + HR-friendly
   attendance analytics. Admin sees everything; a location-scoped supervisor
   sees only punches at their assigned sites (enforced server-side). */
export default function SystemHealthPage() {
  const { t } = useLang();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-dashboard"],
    queryFn: async () => (await api.get("/admin/dashboard")).data,
    refetchInterval: 60000,
  });

  return (
    <AppShell>
      <div className="space-y-6" data-testid="system-health-page">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{t("systemHealth")}</h1>
          {data?.scoped && (
            <p data-testid="dashboard-scoped-note" className="mt-1 text-xs text-muted-foreground">
              {t("scopedNote")}
            </p>
          )}
        </div>

        {/* Infrastructure status — same card as on the Shifts/Settings page */}
        <SystemHealthCard />
        <FaceEngineLogPanel />

        {isLoading ? (
          <div className="grid gap-3 sm:grid-cols-3">
            {[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
          </div>
        ) : data && (
          <>
            {/* ---- Today at a glance ---- */}
            <section className="space-y-3" data-testid="dashboard-today-section">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                {t("todayGlance")}
              </h2>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                <StatTile icon={ClipboardList} label={t("punchesToday")}
                  value={data.today.punches} testId="stat-punches-today" />
                <StatTile icon={LogIn} label={t("clockedInNow")}
                  value={data.today.clockedInNow} testId="stat-clocked-in-now" />
                <StatTile icon={AlarmClock} label={t("lateArrivalsLabel")}
                  value={data.today.lateArrivals}
                  tone={data.today.lateArrivals > 0 ? "warn" : "default"}
                  testId="stat-late-arrivals" />
                <StatTile icon={LogOut} label={t("earlyDeparturesLabel")}
                  value={data.today.earlyDepartures}
                  tone={data.today.earlyDepartures > 0 ? "warn" : "default"}
                  testId="stat-early-departures" />
                <StatTile icon={ListChecks} label={t("pendingReviewLabel")}
                  value={data.today.pendingReview}
                  tone={data.today.pendingReview > 0 ? "warn" : "default"}
                  testId="stat-pending-review" />
              </div>
            </section>

            {/* ---- This month ---- */}
            <section className="space-y-3" data-testid="dashboard-month-section">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                {t("thisMonth")} · <span className="font-mono">{data.month.month}</span>
                <span className="ml-2 normal-case tracking-normal">
                  ({data.month.activeStaff} {t("activeStaffLabel")})
                </span>
              </h2>
              {data.month.punches === 0 ? (
                <p data-testid="dashboard-month-empty"
                  className="rounded-xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">
                  {t("noDataMonth")}
                </p>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                  <StatTile icon={CalendarCheck} label={t("attendanceRate")}
                    value={pct(data.month.attendanceRate)}
                    sub={t("attendanceRateHint")} testId="stat-attendance-rate" />
                  <StatTile icon={TrendingUp} label={t("onTimeRate")}
                    value={pct(data.month.onTimeRate)}
                    sub={t("onTimeRateHint")} testId="stat-on-time-rate" />
                  <StatTile icon={AlarmClock} label={t("lateEventsLabel")}
                    value={data.month.lateEvents}
                    tone={data.month.lateEvents > 0 ? "warn" : "default"}
                    testId="stat-late-events" />
                  <StatTile icon={Timer} label={t("avgMinutesLateLabel")}
                    value={data.month.avgMinutesLate ?? "–"} testId="stat-avg-min-late" />
                  <StatTile icon={ClipboardList} label={t("punchesCol")}
                    value={data.month.punches} testId="stat-month-punches" />
                </div>
              )}
            </section>

            {/* ---- Weekly trend (last 12 weeks) ---- */}
            <Card data-testid="dashboard-trend-card" className="shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                  <TrendingUp className="h-4 w-4 text-primary" /> {t("weeklyTrend")}
                  <span className="font-normal text-muted-foreground">· {t("last12Weeks")}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="h-64 pt-1">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={data.trend} margin={{ top: 8, right: 0, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2ecf1" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                    <YAxis yAxisId="late" allowDecimals={false} tick={{ fontSize: 11 }}
                      tickLine={false} axisLine={false} />
                    <YAxis yAxisId="rate" orientation="right" domain={[0, 100]} unit="%"
                      tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar yAxisId="late" dataKey="lateCount" name={t("lateEventsLabel")}
                      fill="#d97706" radius={[4, 4, 0, 0]} barSize={18} />
                    <Line yAxisId="rate" type="monotone" dataKey="attendanceRate"
                      name={t("attendanceRate")} stroke="#0A85A4" strokeWidth={2}
                      dot={{ r: 2.5 }} connectNulls />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <div className="grid gap-4 lg:grid-cols-2">
              {/* ---- Face verification ---- */}
              <Card data-testid="dashboard-face-card" className="shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                    <ScanFace className="h-4 w-4 text-primary" /> {t("faceVerification")}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 pt-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-semibold tabular-nums" data-testid="face-pass-rate">
                      {pct(data.face.passRate)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {t("facePassRate")} · {t("faceOfPunches")}
                    </span>
                  </div>
                  <Progress value={data.face.passRate ?? 0} className="h-2" />
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span data-testid="face-pass-count">
                      <span className="font-semibold text-emerald-700">{data.face.passCount}</span> {t("matchPass")}
                    </span>
                    <span data-testid="face-fail-count">
                      <span className="font-semibold text-rose-700">{data.face.failCount}</span> {t("matchFail")}
                    </span>
                    <span data-testid="face-noface-count">
                      <span className="font-semibold text-amber-700">{data.face.noFaceCount}</span> {t("noFace")}
                    </span>
                    {(data.face.unverifiedCount ?? 0) > 0 && (
                      <span data-testid="face-unverified-count">
                        <span className="font-semibold text-amber-700">{data.face.unverifiedCount}</span> {t("matchUnverified")}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* ---- Top latecomers ---- */}
              <Card data-testid="dashboard-latecomers-card" className="shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                    <Users className="h-4 w-4 text-primary" /> {t("topLatecomers")}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-1">
                  {data.topLatecomers.length === 0 ? (
                    <p data-testid="no-latecomers" className="py-4 text-sm text-emerald-700">
                      {t("noLatecomers")}
                    </p>
                  ) : (
                    <ul className="divide-y divide-border">
                      {data.topLatecomers.map((r) => (
                        <li key={r.userId} data-testid="latecomer-row"
                          className="flex flex-wrap items-center gap-2 py-2 text-sm">
                          <span className="font-medium">{r.userName}</span>
                          <span className="text-xs text-muted-foreground">{r.employeeId}</span>
                          <span className="ml-auto rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-900">
                            {r.lateCount} {t("timesLate")}
                          </span>
                          <span className="text-xs tabular-nums text-muted-foreground">
                            {r.totalMinutesLate} {t("totalMinLate")}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* ---- By location ---- */}
            <Card data-testid="dashboard-bysite-card" className="shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                  <MapPin className="h-4 w-4 text-primary" /> {t("byLocationTab")}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-1">
                {data.bySite.length === 0 ? (
                  <p className="py-4 text-sm text-muted-foreground">{t("noDataMonth")}</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("locationLabel")}</TableHead>
                        <TableHead className="text-right">{t("punchesCol")}</TableHead>
                        <TableHead className="text-right">{t("lateCol")}</TableHead>
                        <TableHead className="text-right">{t("pendingCol")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.bySite.map((s) => (
                        <TableRow key={s.siteName} data-testid="bysite-row">
                          <TableCell className="font-medium">{s.siteName}</TableCell>
                          <TableCell className="text-right tabular-nums">{s.punches}</TableCell>
                          <TableCell className={`text-right tabular-nums ${s.lateCount > 0 ? "font-semibold text-amber-700" : ""}`}>
                            {s.lateCount}
                          </TableCell>
                          <TableCell className={`text-right tabular-nums ${s.pendingCount > 0 ? "font-semibold text-rose-700" : ""}`}>
                            {s.pendingCount}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Hourglass className="h-3 w-3" />
              {t("generatedAt")}: {new Date(data.generatedAt).toLocaleString()}
            </p>
          </>
        )}
      </div>
    </AppShell>
  );
}
